"use client";
import { UseEmblaCarouselType } from "embla-carousel-react";
import {
  useParams,
  usePathname,
  useRouter,
  useSearchParams,
} from "next/navigation";
import qs from "qs";
import { useCallback, useEffect, useRef, useState, useTransition } from "react";
import useSWR, { Fetcher, Key, SWRConfiguration, SWRResponse } from "swr";
import {
  AGENCY_DISABLED_BEFORE_DATE,
  ASIA_DASHBOARD_DISABLED_BEFORE_DATE,
  ASIA_DASHBOARD_DISABLED_MONTH_BEFORE_DATE,
  ASIA_DISABLED_BEFORE_DATE,
  asiaList,
  CountryCode,
  DISABLED_BEFORE_DATE,
  EUROPE_DISABLED_BEFORE_DATE,
  europeList,
  JP_DISABLED_BEFORE_DATE,
  MX_DISABLED_BEFORE_DATE,
  PROMOTION_DISABLED_BEFORE_DATE,
  VN_DISABLED_BEFORE_DATE,
} from "@/00-shared/const";
import { useUserInfo } from "@/app/[locale]/hooks";
import { countryStore } from "../components/MultiCountrySelect";
import { useQueryState } from "nuqs";
import { DateType } from "../widgets/entity/DatePicker";
import { getBlobUrl } from "./helper";
import Player, { IPlayerOptions } from "xgplayer";
import Mp4Plugin from "xgplayer-mp4";
import { toast as toastSonner } from "sonner";

const expiredTime = 60 * 60 * 1000;

const getDisabledDate = (isDaily?: boolean, countryCode?: string) => {
  if (isDaily && countryCode === CountryCode.VN) {
    return VN_DISABLED_BEFORE_DATE;
  }
  return ASIA_DISABLED_BEFORE_DATE;
};

export const setParams = (key: string, value: unknown) => {
  const searchParams = window.location.search;
  const parsedParams = qs.parse(searchParams);
  window.history.replaceState(
    null,
    "",
    `?${qs.stringify(
      {
        ...parsedParams,
        [key]: value,
      },
      { arrayFormat: "indices" },
    )}`,
  );
};

export const useURLState = () => {
  const searchParams = useSearchParams().toString();
  const parsedParams = qs.parse(searchParams);
  const set = (key: string, value: unknown) => {
    // 这里重新读query参数，不然如果多次调用replaceState会拿不到最新对state
    const cuurentParsedParams = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });
    const needResetPageIndex = key !== "pageIndex";
    const newParams = qs.stringify(
      {
        ...cuurentParsedParams,
        pageIndex: needResetPageIndex ? 0 : undefined,
        [key]: value,
      },
      {
        arrayFormat: "indices",
      },
    );
    window.history.replaceState(null, "", `?${newParams}`);
    return;
  };
  const get = (key: string) => {
    return parsedParams[key];
  };
  return { get, set, searchParams };
};

export interface UseHoverOptions {
  mouseEnterDelayMS?: number;
  mouseLeaveDelayMS?: number;
}

export type HoverProps = Pick<
  React.HTMLAttributes<HTMLElement>,
  "onMouseEnter" | "onMouseLeave"
>;

export function useHover({
  mouseEnterDelayMS = 0,
  mouseLeaveDelayMS = 0,
}: UseHoverOptions = {}): [boolean, HoverProps] {
  const [isHovering, setIsHovering] = useState(false);
  let mouseEnterTimer: ReturnType<typeof setTimeout> | undefined;
  let mouseOutTimer: ReturnType<typeof setTimeout> | undefined;
  return [
    isHovering,
    {
      onMouseEnter: () => {
        clearTimeout(mouseOutTimer);
        mouseEnterTimer = setTimeout(
          () => setIsHovering(true),
          mouseEnterDelayMS,
        );
      },
      onMouseLeave: () => {
        clearTimeout(mouseEnterTimer);
        mouseOutTimer = setTimeout(
          () => setIsHovering(false),
          mouseLeaveDelayMS,
        );
      },
    },
  ];
}

type EmblaCarouselType = UseEmblaCarouselType[1];

type UseCarouselPickerType = {
  selectedIndex: number;
  scrollSnaps: number[];
  onPick: (index: number) => void;
};

export const useCarouselPicker = (
  emblaApi: EmblaCarouselType | undefined,
  onClick?: (emblaApi: EmblaCarouselType) => void,
): UseCarouselPickerType => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onPick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
      if (onClick) onClick(emblaApi);
    },
    [emblaApi, onClick],
  );

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi!.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi!.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);

    emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  return {
    selectedIndex,
    scrollSnaps,
    onPick,
  };
};

export async function fetchFavorityTable(
  userId: string,
  accountId: number,
  platformType?: string,
) {
  return fetch(
    `/api/v2/account/collection?user_id=${userId}&account_id=${accountId}&platform_type=${
      platformType || ""
    }`,
    {
      headers: {
        "x-biyi-resource-key": "user.personal.account.collection",
      },
    },
  )
    .then((res) => res.json())
    .then((data) => data?.collect_entity_list);
}
export const STAR_FETCH_KEY = "/api/favorites";
export function useCollectedEntity(platformType?: string) {
  const { user, userAuthInfo: authInfo, isLogin } = useUserInfo();
  const countryCode = countryStore((state) => state.countryCode);
  const { data = [], ...rest } = useSWR(
    isLogin ? STAR_FETCH_KEY + countryCode + platformType : null,
    () =>
      fetchFavorityTable(
        user?.id ?? "",
        authInfo?.account_id ?? 0,
        platformType ?? "",
      ),
    {
      revalidateOnFocus: false,
      keepPreviousData: true,
      revalidateIfStale: false,
    },
  );
  return {
    data,
    ...rest,
  };
}
export function useDisabledDate(isDashboard?: boolean, isDaily?: boolean) {
  const disableList = [
    "product_promotion_view",
    "shop_most_promoted_view",
    "creator_ecommerce_creators_view",
  ];
  const params = useParams();
  const path = usePathname();
  const [granularity] = useQueryState("granularity");
  const isMonthly = granularity === DateType.Month;
  const countryCode = countryStore((state) => state.countryCode);
  if (path.includes("agency-dashboard")) {
    return AGENCY_DISABLED_BEFORE_DATE;
  }
  if (countryCode === CountryCode.JP) {
    return JP_DISABLED_BEFORE_DATE;
  }
  if (isDashboard) {
    if (europeList.includes(countryCode as CountryCode)) {
      return EUROPE_DISABLED_BEFORE_DATE;
    }
    if (countryCode === CountryCode.MX) {
      return MX_DISABLED_BEFORE_DATE;
    }
    if (asiaList.includes(countryCode as CountryCode)) {
      if (isMonthly) {
        return ASIA_DASHBOARD_DISABLED_MONTH_BEFORE_DATE;
      }
      return ASIA_DASHBOARD_DISABLED_BEFORE_DATE;
    }
  }
  if (countryCode !== CountryCode.US) {
    return getDisabledDate(isDaily, countryCode);
  }
  const entityViewName = params?.entityAndView?.[1];
  const disabledDate =
    disableList.includes(entityViewName) || isDashboard
      ? PROMOTION_DISABLED_BEFORE_DATE
      : DISABLED_BEFORE_DATE;
  return disabledDate;
}
export function useNavigatePreviousUrlWithQuery() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const previousUrlRef = useRef<string | null>(null);
  const currentUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const currentFullPath =
      pathname + (searchParams.toString() ? `?${searchParams.toString()}` : "");

    if (searchParams.toString()) {
      if (currentFullPath !== currentUrlRef.current) {
        previousUrlRef.current = currentUrlRef.current;
        currentUrlRef.current = currentFullPath;
      }
    }
  }, [pathname, searchParams]);

  const navigateToPreviousUrl = useCallback(() => {
    if (previousUrlRef.current) {
      router.push(previousUrlRef.current, { scroll: false });
    }
  }, [router]);

  return {
    previousUrl: previousUrlRef.current,
    navigateToPreviousUrl,
  };
}
export const useBtnCountDown = (seconds: number) => {
  const [countDown, setCountDown] = useState(seconds);
  const [startCountDown, setStartCountDown] = useState(false);
  useEffect(() => {
    if (!startCountDown) {
      return;
    }
    const interval = setInterval(() => {
      setCountDown((preCount) => {
        if (preCount <= 0) {
          setStartCountDown(false);
          clearInterval(interval);
          return seconds;
        }
        return preCount - 1;
      });
    }, 1000);
    return () => {
      clearInterval(interval);
      setStartCountDown(false);
    };
  }, [startCountDown, seconds]);
  return { countDown, startCountDown, setStartCountDown };
};
export const useRefresh = () => {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  function refresh() {
    startTransition(() => {
      router.refresh();
    });
  }
  return {
    isReFreshing: isPending,
    refresh,
  };
};
export function useExpiredSWR<Data = any, Error = any>(
  key: Key,
  fetcher: Fetcher<Data>,
  config?: SWRConfiguration<Data, Error>,
): SWRResponse<Data, Error> {
  const flag = Math.floor(Date.now() / expiredTime);

  let newKey: Key;
  if (Array.isArray(key)) {
    newKey = [...key, flag];
  } else if (key !== null && key !== undefined) {
    newKey = `${key}-${flag}`;
  } else {
    newKey = key;
  }

  return useSWR(newKey, fetcher, config);
}

export function useIsSafari() {
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes("safari") && !userAgent.includes("chrome");
}

export function useIsIOS() {
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes("iphone") || userAgent.includes("ipad");
}

export function useIsIOSOrSafari() {
  const isIOS = useIsIOS();
  const isSafari = useIsSafari();
  return isIOS || isSafari;
}

// 适配ios和safari
export function useVideoAdaptor({
  s3Url,
  playerConfig,
  onInit,
}: {
  s3Url?: string;
  playerConfig: IPlayerOptions;
  onInit?: (player: Player) => void;
}) {
  const isIOSOrSafari = useIsIOSOrSafari();
  const playerRef = useRef<Player | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const [blobLoading, setBlobLoading] = useState(true);
  useEffect(() => {
    if (!s3Url) {
      return;
    }

    // 清理之前的播放器实例
    if (playerRef.current) {
      playerRef.current.destroy();
      playerRef.current = null;
    }
    getBlobUrl(s3Url, isIOSOrSafari).then((blobUrl) => {
      setBlobLoading(false);
      blobUrlRef.current = blobUrl;
      playerRef.current = new Player({
        ...playerConfig,
        url: blobUrl,
        plugins: isIOSOrSafari ? [Mp4Plugin] : [],
      });
      if (onInit) {
        onInit(playerRef.current);
      }
    });

    // 清理函数
    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      if (blobUrlRef.current && isIOSOrSafari) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [s3Url]);
  return {
    playerRef,
    blobUrlRef,
    blobLoading,
  };
}

export function useLoginChecker() {
  const { user } = useUserInfo();
  const router = useRouter();

  const handleCheckLogin = useCallback(
    (toast?: boolean | string) => {
      if (!user) {
        if (toast !== false) {
          toastSonner.info(toast ?? "请先登录", {
            position: "top-center",
          });
        }

        router.push("/login");

        return false;
      }

      return true;
    },
    [router, user],
  );

  return handleCheckLogin;
}
